import React, { useCallback, useEffect, useState } from "react";
import {
    Alert, Box, Button, Card, CardContent, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
    IconButton, InputAdornment, Link, MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import moment from "moment";
import { SaveButton } from "app/shared/ui";
import apiAccessServices from "app/services/api-access-services";
import { requestErrors } from "app/services/automations/generated-file-services";

const EXPIRATIONS = [
    { value: 90, label: "90 días" },
    { value: 30, label: "30 días" },
    { value: 365, label: "1 año" },
    { value: "", label: "Sin vencimiento" },
];

const formatDate = (value) => (value ? moment(value).format("DD-MM-YYYY HH:mm") : "nunca");

const copy = (text) => navigator.clipboard?.writeText(text);

const CopyField = ({ label, value, helperText }) => (
    <TextField
        fullWidth size="small" label={label} value={value} helperText={helperText}
        InputProps={{
            readOnly: true,
            sx: { fontFamily: "monospace" },
            endAdornment: (
                <InputAdornment position="end">
                    <Tooltip title="Copiar"><IconButton size="small" onClick={() => copy(value)}><ContentCopyOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                </InputAdornment>
            ),
        }}
    />
);

const companyNames = (companies) => companies.map((company) => company.name).join(", ");

const EMPTY_FORM = { name: "", expiresInDays: 90, write: false };

const WriteChip = () => (
    <Tooltip title="Puede crear y editar dashboards y reglas de alerta">
        <Chip size="small" label="Puede editar" color="warning" variant="outlined" sx={{ ml: 0.75, height: 20 }} />
    </Tooltip>
);

// «Mi perfil» → API y MCP: aparece si alguna de las empresas del usuario tiene el plugin «API» o «MCP». La URL del MCP
// para Claude o ChatGPT, la documentación de la API, los tokens personales y las aplicaciones conectadas por OAuth.
const ApiAccessSection = () => {
    const [tokens, setTokens] = useState([]);
    const [meta, setMeta] = useState(null);
    const [connections, setConnections] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [created, setCreated] = useState(null);

    const load = useCallback(() => {
        apiAccessServices.listTokens().then((resp) => { setTokens(resp.data || []); setMeta(resp.meta || null); }).catch(() => {});
        apiAccessServices.listConnections().then((resp) => setConnections(resp.data || [])).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!meta || (!meta.api?.length && !meta.mcp?.length)) return null;

    const createToken = async () => {
        setCreating(true);
        setError(null);
        try {
            setCreated(await apiAccessServices.createToken(form));
            setForm(EMPTY_FORM);
            load();
        } catch (requestError) {
            setError(requestErrors(requestError, "No se pudo crear el token."));
        } finally {
            setCreating(false);
        }
    };

    const revokeToken = async (token) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`¿Revocar el token «${token.name}»? Lo que lo use dejará de funcionar.`)) return;
        await apiAccessServices.revokeToken(token.id);
        load();
    };

    const revokeConnection = async (connection) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`¿Quitarle el acceso a «${connection.name}»?`)) return;
        await apiAccessServices.revokeConnection(connection.id);
        load();
    };

    return (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h4" color="primary">API y MCP</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Conecta tus datos de Xpit Connect con asistentes como Claude o ChatGPT, o con tus propios sistemas. Solo verán lo que
                    tu perfil permite, y solo crearán o editarán dashboards y reglas de alerta si se lo permites. Nunca podrán borrar
                    nada ni operar equipos.
                </Typography>

                <Stack spacing={2.5}>
                    {!!meta.mcp?.length && (
                        <Box>
                            <Typography variant="h5" sx={{ mb: 1 }}>Conectar un asistente (MCP)</Typography>
                            <CopyField label="URL del MCP" value={meta.mcp_url} helperText={`Empresas con MCP: ${companyNames(meta.mcp)}.`} />
                            <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    <li><strong>Claude:</strong> Configuración → Conectores → Agregar conector personalizado, y pega la URL. Te pedirá iniciar sesión en Xpit Connect.</li>
                                    <li><strong>ChatGPT:</strong> Configuración → Conectores (modo desarrollador) → Crear, pega la URL y elige OAuth.</li>
                                    <li><strong>Claude Code o Cursor:</strong> usa la URL con un token personal en el encabezado <code>Authorization: Bearer</code>.</li>
                                </ul>
                            </Typography>
                        </Box>
                    )}

                    {!!meta.api?.length && (
                        <Box>
                            <Typography variant="h5" sx={{ mb: 0.5 }}>API</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Empresas con API: {companyNames(meta.api)}. Usa un token personal en <code>Authorization: Bearer</code>.{" "}
                                <Link href={meta.docs_url} target="_blank" rel="noopener noreferrer">Ver la documentación</Link>.
                            </Typography>
                        </Box>
                    )}

                    <Divider />

                    <Box>
                        <Typography variant="h5" sx={{ mb: 1 }}>Tokens personales</Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
                            <TextField size="small" label="Nombre" placeholder="Ej: Claude Code" value={form.name} sx={{ flex: 1 }}
                                       onChange={(event) => setForm({ ...form, name: event.target.value })} />
                            <TextField select size="small" label="Vence en" value={form.expiresInDays} sx={{ minWidth: 160 }}
                                       onChange={(event) => setForm({ ...form, expiresInDays: event.target.value })}>
                                {EXPIRATIONS.map((option) => <MenuItem key={option.label} value={option.value}>{option.label}</MenuItem>)}
                            </TextField>
                            <SaveButton loading={creating} disabled={creating || !form.name.trim()} startIcon={<KeyOutlinedIcon />} onClick={createToken}>
                                Crear token
                            </SaveButton>
                        </Stack>
                        {meta.can_write && (
                            <FormControlLabel
                                sx={{ mt: 0.5, alignItems: "flex-start" }}
                                control={<Checkbox size="small" sx={{ pt: 0.25 }} checked={form.write} onChange={(event) => setForm({ ...form, write: event.target.checked })} />}
                                label={(
                                    <Typography variant="body2">
                                        Permitir crear y editar dashboards y reglas de alerta
                                        <Typography component="span" variant="caption" color="text.secondary" display="block">
                                            Un cambio que baje la protección de una regla queda propuesto hasta que alguien lo apruebe en Automatizaciones.
                                        </Typography>
                                    </Typography>
                                )}
                            />
                        )}
                        {!!error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
                        <Stack spacing={1} sx={{ mt: 1.5 }}>
                            {!tokens.length && <Typography variant="body2" color="text.secondary">No tienes tokens.</Typography>}
                            {tokens.map((token) => (
                                <Stack key={token.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {token.name} <Typography component="span" variant="caption" sx={{ fontFamily: "monospace" }}>{token.prefix}…</Typography>
                                            {token.scopes?.includes("write") && <WriteChip />}
                                        </Typography>
                                        <Typography variant="caption" color={token.expired ? "error" : "text.secondary"}>
                                            Último uso: {formatDate(token.last_used_at)} · {token.expired ? "Vencido" : `Vence: ${token.expires_at ? formatDate(token.expires_at) : "nunca"}`}
                                        </Typography>
                                    </Box>
                                    <Button size="small" color="error" onClick={() => revokeToken(token)}>Revocar</Button>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>

                    {!!connections.length && (
                        <Box>
                            <Typography variant="h5" sx={{ mb: 1 }}>Aplicaciones conectadas</Typography>
                            <Stack spacing={1}>
                                {connections.map((connection) => (
                                    <Stack key={connection.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>{connection.name}{connection.can_write && <WriteChip />}</Typography>
                                            <Typography variant="caption" color="text.secondary">Conectada el {formatDate(connection.connected_at)}</Typography>
                                        </Box>
                                        <Button size="small" color="error" onClick={() => revokeConnection(connection)}>Quitar acceso</Button>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </CardContent>

            <Dialog open={!!created} onClose={() => setCreated(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Token «{created?.name}» creado</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>Cópialo ahora: por seguridad no se vuelve a mostrar.</Alert>
                    <CopyField label="Token" value={created?.token || ""} />
                </DialogContent>
                <DialogActions><Button variant="contained" onClick={() => setCreated(null)}>Listo, lo copié</Button></DialogActions>
            </Dialog>
        </Card>
    );
};

export default ApiAccessSection;
